import { useLocalStorage } from '@vueuse/core';
import { nextTick } from 'vue';

import { isImpactfulQuizQuestion, questions, QuizResults } from '@/data/quiz/onsiteOffer';
import { setCookie } from '@/utils/isomorphic/cookie';

export interface OnsiteOfferConfig {
  initialScreen?: {
    buttonCta: string;
    caption: string;
    couponToken: string;
    headline: string;
    logoImageSrc?: string;
    mainImageDesktopSrc: string;
    mainImageMobileSrc: string;
    stickyBubbleSelector: string;
  };
  secondaryScreen?: {
    attentiveSignUpSourceIdExisting: string;
    attentiveSignUpSourceIdNetNew: string;
    buttonCta: string;
    caption: string;
    headlineExisting: string;
    headlineNetNew: string;
    mainImageDesktopSrc: string;
    mainImageMobileSrc: string;
  };
  settings?: {
    analyticsSource?: string;
    newContactNotification?: string;
    existingContactNotification?: string;
  };
  isOpen: boolean;
}

export function useOnsiteOffer() {
  const config = useLocalStorage<OnsiteOfferConfig>('onsiteOffer.config', {
    isOpen: false,
  });

  const results = useLocalStorage<QuizResults>('onsiteOffer.results', () => {
    const [firstQuestion, secondQuestion] = questions.filter(isImpactfulQuizQuestion);
    return {
      selections: [firstQuestion, secondQuestion],
    };
  });

  function closeModal() {
    config.value.isOpen = false;
  }

  function openOnsiteOffer(
    initialScreen: OnsiteOfferConfig['initialScreen'],
    secondaryScreen?: OnsiteOfferConfig['secondaryScreen'],
    settings?: OnsiteOfferConfig['settings'],
  ) {
    config.value.settings = settings;
    config.value.initialScreen = initialScreen;
    config.value.secondaryScreen = secondaryScreen;
    config.value.isOpen = true;
  }

  function rescindOffer(shouldCloseModal = true) {
    if (shouldCloseModal) {
      closeModal();
    }

    setCookie('dy_onsite_offer_success', 'true');

    if (config.value.initialScreen?.stickyBubbleSelector) {
      document
        .querySelector(config.value.initialScreen.stickyBubbleSelector)
        ?.classList.add('hidden');
    }
  }

  /**
   * This is just a helper method that wraps `nextTick` to make the code self documenting at the call sites.
   *
   * The reason we need to call nextTick at the call sites is because after updating `results`, we generally emit
   * an event that leads to the destruction of the component and something related to that timing was keeping storage
   * from being updated.  `useLocalStorage` watches for changes on the reactive property, but it wasn't hearing the
   * change. Using `nextTick` fixes this.
   */
  async function waitForStorageUpdate() {
    await nextTick();
  }

  return {
    closeModal,
    config,
    openOnsiteOffer,
    rescindOffer,
    results,
    waitForStorageUpdate,
  };
}
